import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Hub from "@/pages/Hub";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import Dashboard from "@/pages/Dashboard";
import Properties from "@/pages/Properties";
import PropertyEditor from "@/pages/PropertyEditor";
import PropertyPreview from "@/pages/PropertyPreview";
import CalendarPage from "@/pages/CalendarPage";
import Messages from "@/pages/Messages";
import Delegates from "@/pages/Delegates";
import Settings from "@/pages/Settings";
import Upgrade from "@/pages/Upgrade";
import Admin from "@/pages/Admin";
import ImpersonateUsers from "@/pages/ImpersonateUsers";
import AdminTickets from "@/pages/AdminTickets";
import MotorHealth from "@/pages/MotorHealth";
import Tickets from "@/pages/Tickets";
import Referral from "@/pages/Referral";
import More from "@/pages/More";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import SponsorForm from "@/pages/SponsorForm";
import { PrivacyPolicy, CookiePolicy, Terms } from "@/pages/Legal";
import AiReportLanding from "@/pages/AiReportLanding";
import AiReportApp from "@/pages/AiReportApp";
import AiReportEditor from "@/pages/AiReportEditor";
import AiReportPresent from "@/pages/AiReportPresent";
import AiReportGo from "@/pages/AiReportGo";
import AiReportJoin from "@/pages/AiReportJoin";
import EventJoin from "@/pages/EventJoin";
import ChooseServices from "@/pages/ChooseServices";
import AiReportSession from "@/pages/AiReportSession";

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  );
}

function Protected({ children }) {
  const { user, org } = useAuth();
  const loc = useLocation();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin" && (!user.org_id || loc.pathname.startsWith("/admin"))) {
    if (loc.pathname.startsWith("/admin")) return <Layout>{children}</Layout>;
    return <Navigate to="/admin" replace />;
  }
  if (org && org.usable === false && loc.pathname !== "/upgrade") {
    return <Navigate to="/upgrade" replace />;
  }
  return <Layout>{children}</Layout>;
}

function ProtectedBare({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/home"} replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Emergent Google Auth returns to {origin}/home#session_id=... — handle it before any route/guard runs.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/termini" element={<Terms />} />
      <Route path="/m/:id" element={<SponsorForm />} />
      <Route path="/ai-report" element={<AiReportLanding />} />
      <Route path="/e/:slug" element={<EventJoin />} />
      <Route path="/r" element={<AiReportJoin />} />
      <Route path="/r/:code" element={<AiReportJoin />} />
      <Route path="/ai-report/app" element={<Protected><AiReportApp /></Protected>} />
      <Route path="/ai-report/edit/:id" element={<Protected><AiReportEditor /></Protected>} />
      <Route path="/ai-report/session/:sid" element={<Protected><AiReportSession /></Protected>} />
      <Route path="/ai-report/present/:sid" element={<ProtectedBare><AiReportPresent /></ProtectedBare>} />
      <Route path="/ai-report/go/:id" element={<ProtectedBare><AiReportGo /></ProtectedBare>} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/home" element={<Protected><Hub /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/properties" element={<Protected><Properties /></Protected>} />
      <Route path="/properties/:id" element={<Protected><PropertyEditor /></Protected>} />
      <Route path="/properties/:id/preview" element={<Protected><PropertyPreview /></Protected>} />
      <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/messages" element={<Protected><Messages /></Protected>} />
      <Route path="/delegates" element={<Protected><Delegates /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/more" element={<Protected><More /></Protected>} />
      <Route path="/scegli-servizi" element={<Protected><ChooseServices /></Protected>} />
      <Route path="/upgrade" element={<Protected><Upgrade /></Protected>} />
      <Route path="/support" element={<Protected><Tickets /></Protected>} />
      <Route path="/referral" element={<Protected><Referral /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/admin/impersonate" element={<Protected><ImpersonateUsers /></Protected>} />
      <Route path="/admin/tickets" element={<Protected><AdminTickets /></Protected>} />
      <Route path="/admin/motor" element={<Protected><MotorHealth /></Protected>} />
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
